function isObjectRecord(input: unknown): input is Record<string, unknown> {
  return typeof input === 'object' && input !== null
}

function isUrlLikeObject(input: Record<string, unknown>): input is { href: string } {
  return typeof input.href === 'string' && input.href.length > 0
}

function hasValueProperty(input: Record<string, unknown>): input is { value: unknown } {
  return 'value' in input
}

function isNumericKeyObject(input: Record<string, unknown>): boolean {
  return Object.keys(input).length > 0 && Object.keys(input).every((key) => /^\d+$/.test(key))
}

function stringFromNumericObject(input: Record<string, unknown>): string | undefined {
  if (!isNumericKeyObject(input)) return undefined
  const sorted = Object.keys(input).sort((a, b) => Number(a) - Number(b))
  const joined = sorted
    .map((key) => input[key])
    .filter((value) => typeof value === 'string')
    .join('')
  return joined.length > 0 ? joined : undefined
}

function ensureAbsoluteBase(candidate: string): string | undefined {
  const trimmed = candidate.trim()
  if (!trimmed) return undefined
  const attempts = [trimmed]
  if (!/^https?:\/\//i.test(trimmed)) attempts.push(`http://${trimmed}`)
  for (const attempt of attempts) {
    try {
      return new URL(attempt).toString()
    } catch {}
  }
  return undefined
}

function appendSegmentToBase(base: string, segment: string): string | undefined {
  const absoluteBase = ensureAbsoluteBase(base)
  if (!absoluteBase) return undefined
  const sanitizedSegment = segment.replace(/^\/+/, '')
  const baseWithSlash = absoluteBase.endsWith('/') ? absoluteBase : `${absoluteBase}/`
  try {
    return new URL(sanitizedSegment, baseWithSlash).toString()
  } catch {
    return undefined
  }
}

export function resolveRuntimeShapeUrl(candidate: unknown): string | undefined {
  if (!candidate) return undefined

  if (typeof candidate === 'string') {
    const trimmed = candidate.trim()
    return trimmed.length > 0 ? trimmed : undefined
  }

  if (typeof URL !== 'undefined' && candidate instanceof URL) return candidate.toString()

  if (!isObjectRecord(candidate)) return undefined

  if (isUrlLikeObject(candidate)) return candidate.href

  if (hasValueProperty(candidate)) return resolveRuntimeShapeUrl(candidate.value)

  const stringFromObject = stringFromNumericObject(candidate)
  if (stringFromObject) return stringFromObject

  return undefined
}

type ShapeEnvSource = Record<string, string | undefined>

function getProcessEnv(): ShapeEnvSource | undefined {
  if (typeof process === 'undefined') return undefined
  return process.env as ShapeEnvSource
}

function buildPortFallback(port?: string): string | undefined {
  if (!port) return undefined
  const normalizedPort = port.trim()
  if (!normalizedPort) return undefined
  return appendSegmentToBase(`http://localhost:${normalizedPort}`, 'v1/shape')
}

export function inferRuntimeShapeUrlFromEnv(
  envSource?: ShapeEnvSource,
  options?: { shapePath?: string },
): string | undefined {
  const source = envSource ?? getProcessEnv()
  if (!source) return undefined

  const directCandidates = [
    source.NUXT_PUBLIC_REACTIVE_DB_SHAPE_URL,
    source.NUXT_REACTIVE_DB_SHAPE_URL,
    source.REACTIVEDB_SHAPE_URL,
    source.ELECTRIC_SHAPE_URL,
  ]

  for (const candidate of directCandidates) {
    const resolved = resolveRuntimeShapeUrl(candidate)
    if (resolved) return resolved
  }

  const shapePath = options?.shapePath ?? 'v1/shape'
  const baseCandidates = [
    source.ELECTRIC_CADDY_URL,
    source.PUBLIC_ELECTRIC_URL,
    source.ELECTRIC_PUBLIC_URL,
    source.ELECTRIC_URL,
    source.ELECTRIC_BASE_URL,
  ]

  for (const baseCandidate of baseCandidates) {
    if (!baseCandidate) continue
    const appended = appendSegmentToBase(baseCandidate, shapePath)
    if (appended) return appended
  }

  const portFallback = buildPortFallback(source.ELECTRIC_HTTP_PORT ?? source.ELECTRIC_PORT)
  if (portFallback) return portFallback

  return undefined
}
